/** Helpers for calendar LODGING tab ↔ Tour Planner day data. */
import { normalizeEmail } from './tourPartyMemberUtils';

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function venueHasLodgingFlag(venue) {
  const bs = String(venue?.backstage || '').toLowerCase();
  return bs.includes('lodging') || bs.includes('hotel') || bs.includes('bed');
}

export function getVenueCoords(venue) {
  if (!venue) return null;
  const lat = Number(venue.latitude ?? venue.lat);
  const lng = Number(venue.longitude ?? venue.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

export function getBusCallCoords(busCall) {
  if (!busCall) return null;
  const lat = Number(busCall.lat);
  const lng = Number(busCall.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

export function findTourDayIndexByDate(tour, dateKey) {
  const days = tour?.days || [];
  return days.findIndex((d) => String(d?.scheduledDate || '').trim() === String(dateKey || '').trim());
}

export function findTourDayByDate(tour, dateKey) {
  const index = findTourDayIndexByDate(tour, dateKey);
  if (index < 0) return { day: null, dayIndex: -1 };
  return { day: tour.days[index], dayIndex: index };
}

export function getLodgingVenueForDay(day) {
  if (!day?.venues?.length) return null;
  const lodgingId = String(day.lodgingVenueId || '').trim();
  if (lodgingId) {
    return day.venues.find((v) => String(v?.id) === lodgingId) || null;
  }
  return null;
}

/** Last meaningful end-of-day location: lodging venue, last venue, or bus call. */
export function getDayEndStop(day) {
  if (!day) return null;
  const lodging = getLodgingVenueForDay(day);
  if (lodging) {
    return { type: 'lodging', label: lodging.name || 'Lodging', coords: getVenueCoords(lodging), venue: lodging };
  }
  const venues = day.venues || [];
  for (let i = venues.length - 1; i >= 0; i -= 1) {
    const coords = getVenueCoords(venues[i]);
    if (coords) {
      return { type: 'venue', label: venues[i].name || 'Stop', coords, venue: venues[i] };
    }
  }
  if (day.busCall?.lat) {
    return {
      type: 'busCall',
      label: day.busCall.name || 'Bus call',
      coords: getBusCallCoords(day.busCall),
      busCall: day.busCall,
    };
  }
  return null;
}

export function estimateTravelBetween(fromCoords, toCoords, mode = 'drive') {
  if (!fromCoords || !toCoords) return null;
  const { lat: lat1, lng: lng1 } = fromCoords;
  const { lat: lat2, lng: lng2 } = toCoords;
  if (Math.abs(lat1 - lat2) < 0.0001 && Math.abs(lng1 - lng2) < 0.0001) {
    return { same: true, miles: '0', timeLabel: 'Same location', modeLabel: 'Same spot' };
  }
  const meters = haversineMeters(lat1, lng1, lat2, lng2);
  const milesNum = meters / 1609.344;
  const miles = milesNum.toFixed(1);
  const speedMph = { drive: 55, bus: 45, walk: 3, train: 70, flight: 480 }[mode] || 55;
  const hrs = milesNum / speedMph;
  const h = Math.floor(hrs);
  const m = Math.round((hrs - h) * 60);
  const timeLabel = h > 0 ? `~${h}h ${m}m` : `~${m}m`;
  const modeLabel = { drive: 'Drive', bus: 'Bus', walk: 'Walk', train: 'Train', flight: 'Flight' }[mode] || mode;
  return { same: false, miles, timeLabel, modeLabel };
}

export function getInterDayTravelSummary(tour, dayIndex) {
  if (!tour?.days || dayIndex <= 0) return null;
  const prevDay = tour.days[dayIndex - 1];
  const day = tour.days[dayIndex];
  const fromStop = getDayEndStop(prevDay);
  const toStop = getDayEndStop(day) || (() => {
    const firstVenue = (day?.venues || []).find((v) => getVenueCoords(v));
    if (!firstVenue) return null;
    return { type: 'venue', label: firstVenue.name || 'First stop', coords: getVenueCoords(firstVenue), venue: firstVenue };
  })();
  if (!fromStop?.coords || !toStop?.coords) return null;
  const mode = (tour.interDayLegModes || [])[dayIndex - 1] || 'drive';
  const estimate = estimateTravelBetween(fromStop.coords, toStop.coords, mode);
  if (!estimate) return null;
  return {
    fromLabel: fromStop.label,
    toLabel: toStop.label,
    mode,
    ...estimate,
  };
}

export function normalizePinnedStops(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((s, i) => ({
      id: String(s?.id || `pin-${i}`),
      name: String(s?.name || '').trim(),
      location: String(s?.location || '').trim(),
      note: String(s?.note || '').trim(),
      participants: Array.isArray(s?.participants) ? [...s.participants] : [],
    }))
    .filter((s) => s.name || s.location || s.note);
}

export function getStopParticipants(day, stopKey) {
  const map = day?.stopParticipation && typeof day.stopParticipation === 'object' ? day.stopParticipation : {};
  const list = map[stopKey];
  return Array.isArray(list) ? list : [];
}

/** Map stored participant ids/emails to tour party member records for avatars. */
export function resolveParticipantMembers(participantIds, members = []) {
  if (!Array.isArray(participantIds) || !participantIds.length) return [];
  const byId = new Map();
  const byEmail = new Map();
  members.forEach((member) => {
    const id = String(member?.id || '').trim();
    if (id) byId.set(id, member);
    const email = normalizeEmail(member?.email);
    if (email) byEmail.set(email, member);
  });

  const seen = new Set();
  const resolved = [];
  participantIds.forEach((rawId) => {
    const id = String(rawId || '').trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    const emailKey = normalizeEmail(id);
    const member =
      byId.get(id) ||
      (emailKey ? byEmail.get(emailKey) : null) ||
      (emailKey ? { id, email: emailKey } : { id });
    resolved.push(member);
  });
  return resolved;
}

export function formatLodgingDaySummary(tour, dateKey) {
  const { day, dayIndex } = findTourDayByDate(tour, dateKey);
  if (!day) return '';
  const lines = [];
  const travel = getInterDayTravelSummary(tour, dayIndex);
  if (travel && !travel.same) {
    lines.push(`Travel: ${travel.fromLabel} → ${travel.toLabel} · ${travel.miles} mi · ${travel.timeLabel} (${travel.modeLabel})`);
  }
  const lodging = getLodgingVenueForDay(day);
  if (lodging) lines.push(`Lodging: ${lodging.name || 'Hotel'}${lodging.city ? ` · ${lodging.city}` : ''}`);
  if (day.busCall?.name || day.busCall?.time) {
    lines.push(`Bus call: ${[day.busCall.name, day.busCall.time].filter(Boolean).join(' · ')}`);
  }
  normalizePinnedStops(day.pinnedStops).forEach((pin) => {
    lines.push(`Pin: ${[pin.name, pin.location, pin.note].filter(Boolean).join(' · ')}`);
  });
  return lines.join('\n');
}

export function parseLodgingStays(raw) {
  const s = String(raw || '').trim();
  if (!s) return [];
  try {
    const o = JSON.parse(s);
    if (o && Array.isArray(o.stays)) {
      return o.stays.map((st, i) => ({
        id: String(st?.id || `stay-${i}`),
        name: String(st?.name || '').trim(),
        city: String(st?.city || '').trim(),
        address: String(st?.address || '').trim(),
        parking: String(st?.parking || '').trim(),
        busDrop: String(st?.busDrop || '').trim(),
        busPick: String(st?.busPick || '').trim(),
        notes: String(st?.notes || '').trim(),
      }));
    }
  } catch {
    /* ignore */
  }
  return [];
}

export function serializeLodgingStays(stays) {
  return JSON.stringify({ v: 1, stays: Array.isArray(stays) ? stays : [] });
}

export function emptyLodgingStay() {
  return {
    id: `stay-${Date.now()}`,
    name: '',
    city: '',
    address: '',
    parking: '',
    busDrop: '',
    busPick: '',
    notes: '',
  };
}
